/**
 * "Add chart → From catalog": pick saved charts and drop them on the dashboard.
 *
 * What you add is a **link**, not a copy - the tile draws the catalog chart as
 * it is now, the way a Grafana library panel does (see `linked-chart.ts`). The
 * footer says so, because it is the one thing about this dialog that is not
 * obvious from looking at it.
 *
 * The picker keeps its own tab and search rather than borrowing the Catalog
 * section's atoms. Picking a chart for a dashboard should not leave the Catalog
 * page filtered the next time you visit it.
 *
 * Several charts can be picked at once - building a dashboard is usually "these
 * six" rather than one at a time. Click toggles a card; double-click adds that
 * chart straight away.
 *
 * Templates sort first. A template follows the dashboard toolbar's ship and
 * window, which is what most tiles want; a chart that pinned its context is
 * still offered, and says what it pinned.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { fetchChartThumbnail } from '../../api/endpoints.ts';
import type { CatalogChart } from '../../api/types.ts';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { catalogAvailable } from '../../state/store.ts';
import {
  catalogCharts,
  catalogError,
  catalogLoaded,
  catalogLoading,
  loadCatalog,
  scopedCharts,
  type CatalogScope,
} from '../catalog/catalog-state.ts';
import { chartMatches, chartSummary, isTemplate, shapeSummary } from '../catalog/chart-summary.ts';
import { cachedThumbnail, rememberThumbnail } from '../catalog/thumbnail.ts';
import { plotTypeIcon } from '../plot/plot-type-icons.ts';

export interface PickerCommit {
  charts: CatalogChart[];
}

const SCOPES: ReadonlyArray<{ scope: CatalogScope; label: string }> = [
  { scope: 'mine', label: 'My charts' },
  { scope: 'shared', label: 'Shared with me' },
  { scope: 'public', label: 'Public' },
];

/** Templates first, then most recently modified. */
function pickerOrder(a: CatalogChart, b: CatalogChart): number {
  const t = Number(isTemplate(b)) - Number(isTemplate(a));
  return t || b.updated_at.localeCompare(a.updated_at);
}

@customElement('catalog-picker')
export class CatalogPicker extends LightElement {
  /** Charts already linked on the active tab, so a second copy is a choice. */
  @property({ attribute: false }) linkedIds: ReadonlySet<string> = new Set();

  private store = new StoreController(this, [
    catalogCharts,
    catalogLoaded,
    catalogLoading,
    catalogError,
    catalogAvailable,
  ]);

  @state() private scope: CatalogScope = 'mine';
  @state() private query = '';
  /** Picked chart ids, in the order picked - the order tiles are placed in. */
  @state() private picked: string[] = [];
  /** Whether the scope was chosen by a person, not by the first-load default. */
  private scopeChosen = false;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeydown);
    // Refresh on open: a chart saved a moment ago in Explore should be here.
    if (catalogAvailable.value && !catalogLoading.value) void loadCatalog();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKeydown);
  }

  override firstUpdated(): void {
    this.querySelector<HTMLInputElement>('.cpick-search')?.focus();
  }

  override willUpdate(): void {
    // Open on a tab with something in it. "My charts" is the natural first
    // stop, but for someone who has saved nothing yet it is an empty page in
    // front of a Public tab full of charts they could use.
    if (this.scopeChosen || !catalogLoaded.value) return;
    const first = SCOPES.find((s) => scopedCharts(s.scope).length > 0);
    if (first) this.scope = first.scope;
  }

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.cancel();
  };

  private visible(): CatalogChart[] {
    return scopedCharts(this.scope)
      .filter((c) => chartMatches(c, this.query))
      .sort(pickerOrder);
  }

  private toggle(chart: CatalogChart): void {
    this.picked = this.picked.includes(chart.id)
      ? this.picked.filter((id) => id !== chart.id)
      : [...this.picked, chart.id];
  }

  private cancel(): void {
    this.dispatchEvent(new CustomEvent('picker-close', { bubbles: true, composed: true }));
  }

  private commit(charts: CatalogChart[]): void {
    if (!charts.length) return;
    this.dispatchEvent(
      new CustomEvent<PickerCommit>('picker-commit', {
        detail: { charts },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private commitPicked(): void {
    const byId = new Map(catalogCharts.value.map((c) => [c.id, c]));
    this.commit(this.picked.map((id) => byId.get(id)).filter((c): c is CatalogChart => !!c));
  }

  /* ── Render ───────────────────────────────────────────── */

  private renderBody(): TemplateResult {
    if (!catalogAvailable.value) {
      return html`<p class="cpick-state">The catalog needs the database, and this deployment has none.</p>`;
    }
    if (!catalogLoaded.value) {
      if (catalogError.value) {
        return html`<p class="cpick-state is-error">
          Could not load the catalog: ${catalogError.value}
          <button class="dash-btn" @click=${() => void loadCatalog()}>Try again</button>
        </p>`;
      }
      return html`<p class="cpick-state">
        <span class="spinner" aria-hidden="true"></span> Loading the catalog…
      </p>`;
    }
    const charts = this.visible();
    if (!charts.length) {
      return html`<p class="cpick-state">
        ${this.query
          ? `Nothing here matches "${this.query}".`
          : this.scope === 'mine'
            ? 'You have not saved any charts yet. Save one from Explore with ★ Save.'
            : 'Nothing here yet.'}
      </p>`;
    }
    return html`<div class="cpick-grid">
      ${charts.map(
        (chart) => html`<catalog-pick-card
          .chart=${chart}
          .picked=${this.picked.includes(chart.id)}
          .onDashboard=${this.linkedIds.has(chart.id)}
          @click=${() => this.toggle(chart)}
          @dblclick=${() => this.commit([chart])}
        ></catalog-pick-card>`,
      )}
    </div>`;
  }

  override render(): TemplateResult {
    const count = this.picked.length;
    return html`
      <div class="cbuilder-scrim" @click=${this.cancel}></div>
      <div class="cpick" data-tour="catalog-picker" role="dialog" aria-modal="true" aria-label="Add from catalog">
        <header class="cpick-head">
          <h2>Add from catalog</h2>
          <input
            class="cpick-search"
            type="search"
            placeholder="Search titles, columns, tags…"
            .value=${this.query}
            aria-label="Search the catalog"
            @input=${(e: Event) => {
              this.query = (e.target as HTMLInputElement).value;
            }}
          />
        </header>

        <nav class="cpick-tabs" role="tablist">
          ${SCOPES.map(
            ({ scope, label }) => html`<button
              class="cpick-tab ${scope === this.scope ? 'active' : ''}"
              role="tab"
              aria-selected=${scope === this.scope ? 'true' : 'false'}
              @click=${() => {
                this.scope = scope;
                this.scopeChosen = true;
              }}
            >
              ${label}
              ${catalogLoaded.value
                ? html`<span class="dash-tab-count">${scopedCharts(scope).length}</span>`
                : nothing}
            </button>`,
          )}
        </nav>

        <div class="cpick-body scroll-thin">${this.renderBody()}</div>

        <footer class="cpick-foot">
          <span class="cpick-note">
            Charts stay linked: edits in the catalog show up on the dashboard. Double-click adds one
            straight away.
          </span>
          <span class="cpick-foot-actions">
            <button class="dash-btn" @click=${this.cancel}>Cancel</button>
            <button
              class="dash-btn is-primary"
              ?disabled=${count === 0}
              @click=${this.commitPicked}
            >
              ${count === 0 ? 'Add charts' : count === 1 ? 'Add 1 chart' : `Add ${count} charts`}
            </button>
          </span>
        </footer>
      </div>
    `;
  }
}

/**
 * One chart in the picker: preview, title, what it draws. A lighter cousin of
 * `<catalog-card>` - none of its rename, share or delete, because here a card is
 * something to choose, not something to manage.
 */
@customElement('catalog-pick-card')
export class CatalogPickCard extends LightElement {
  @property({ attribute: false }) chart!: CatalogChart;
  @property({ type: Boolean }) picked = false;
  @property({ type: Boolean }) onDashboard = false;

  @state() private preview: string | null = null;
  private previewFor: string | null = null;
  private observer: IntersectionObserver | null = null;
  private seen = false;

  override connectedCallback(): void {
    super.connectedCallback();
    // Previews load as cards scroll into view, like the Catalog's own grid.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer?.disconnect();
        this.observer = null;
        this.seen = true;
        void this.loadPreview();
      },
      { rootMargin: '200px' },
    );
    this.observer.observe(this);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.observer = null;
  }

  override updated(): void {
    // Lit reuses cards across a search or a tab change; the picture must follow.
    if (this.seen && this.chart && this.previewFor !== this.chart.id) void this.loadPreview();
  }

  private async loadPreview(): Promise<void> {
    const chart = this.chart;
    this.previewFor = chart.id;
    this.preview = null;
    const version = chart.thumbnail_version;
    if (version == null) return;
    const cached = cachedThumbnail(chart.id, version);
    if (cached) {
      this.preview = cached;
      return;
    }
    const url = await fetchChartThumbnail(chart.id);
    if (!url) return;
    rememberThumbnail(chart.id, version, url);
    if (this.chart.id === chart.id) this.preview = url;
  }

  override render(): TemplateResult {
    const chart = this.chart;
    const template = isTemplate(chart);
    return html`
      <button
        class="cpick-card ${this.picked ? 'is-picked' : ''}"
        aria-pressed=${this.picked ? 'true' : 'false'}
        title=${chart.description ?? chart.title}
      >
        <div class="cat-card-preview ${this.preview ? '' : 'is-empty'}">
          ${this.preview
            ? html`<img src=${this.preview} alt="" loading="lazy" />`
            : html`${plotTypeIcon(chart.plot_type, 'cat-card-glyph')}
                <span class="cat-card-plot-type">${chart.plot_type}</span>`}
          ${template
            ? html`<span
                class="cat-card-template"
                title="Template - follows the dashboard's ship and time range"
                >Template</span
              >`
            : nothing}
          <span class="cpick-check" aria-hidden="true">✓</span>
        </div>
        <span class="cpick-card-body">
          <span class="cpick-card-title">
            ${plotTypeIcon(chart.plot_type, 'cat-card-type')}
            <span class="cpick-card-name">${chart.title}</span>
          </span>
          <span class="cpick-card-meta">${template ? shapeSummary(chart) : chartSummary(chart)}</span>
          <span class="cpick-card-foot">
            ${chart.is_owner ? 'Yours' : chart.owner.display_name}
            ${this.onDashboard ? html` · <em>already on this tab</em>` : nothing}
          </span>
        </span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'catalog-picker': CatalogPicker;
    'catalog-pick-card': CatalogPickCard;
  }
}
